import { CheckCheck, ClipboardCopy, MailCheck, Undo2, Download, Eye, FileDown, FilePenLine, FileText, Search, Send, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { EmptyState } from "../components/EmptyState";
import { Modal } from "../components/Modal";
import { ClientSignatureImg } from "../components/ClientSignatureImg";
import { Shell } from "../components/Shell";
import { StatusPill } from "../components/StatusPill";
import { letterKindIcon } from "../lib/icons";
import {
  letters as lettersApi,
  mail as mailApi,
  request,
  type Letter,
} from "../lib/api";

const BUREAU_BUNDLE_KINDS = new Set([
  "letter_605b",
  "late_payment",
  "public_records_dispute",
  "manual",
]);

async function downloadPdf(path: string, fallbackFilename: string) {
  const token =
    typeof window !== "undefined" ? localStorage.getItem("nedm.staff.token") : null;
  const res = await fetch(path, {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let detail = "";
    try {
      detail = (await res.json())?.message || "";
    } catch {
      /* ignore */
    }
    throw new Error(`Download failed (${res.status}) ${detail}`.trim());
  }
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") || "";
  const m = cd.match(/filename="?([^";]+)"?/i);
  const filename = m?.[1] || fallbackFilename;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// Backend statuses on the letter row (per functions/api/letters/[id].ts):
//   drafting -> quality_gate_pending -> approved -> queued_for_mail -> mailed -> responded
// Only `approved` (or anything past it on the ladder) can be queued for mail.
const MAILABLE = new Set(["approved", "queued_for_mail", "ready"]);
const APPROVABLE_FROM = new Set(["drafting", "quality_gate_pending"]);
// Letters that can be recorded as mailed by hand (sent outside the Hub).
// (Not "Queued for mail": cancel the Hub job first, so it can't mail a second copy.)
const MARKABLE = new Set(["drafting", "quality_gate_pending", "approved", "ready"]);

function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
// Hand-marked dates are stored at noon UTC, so read them back as a UTC day;
// carrier timestamps read fine either way at day precision.
function mailDayIso(ts: string): string { return new Date(ts).toISOString().slice(0, 10); }
function mailDay(ts: string, manual = false): string {
  return new Date(ts).toLocaleDateString(undefined, manual ? { timeZone: "UTC" } : undefined);
}

interface LetterDetail extends Letter {
  body_text?: string | null;
  recipient_address?: string | null;
  quality_gate_notes?: string | null;
  approved_at?: string | null;
}

export default function LettersPage() {
  const [rows, setRows] = useState<Letter[]>([]);
  // Default view hides letters that already went out; they stay in the
  // client's file and under "All letters".
  const [status, setStatus] = useState<string>("needs_action");
  const [markDate, setMarkDate] = useState<string | null>(null); // YYYY-MM-DD while the mark-mailed form is open
  const [markNote, setMarkNote] = useState("");
  const [search, setSearch] = useState("");
  // Deep link from a client profile: /letters?client=<id>&name=<label>. Read
  // once on mount so opening "Letters" from a client lands pre-filtered to
  // that client instead of dumping the whole library.
  const [clientFilter, setClientFilter] = useState<{ id: string; name: string } | null>(() => {
    if (typeof window === "undefined") return null;
    const p = new URLSearchParams(window.location.search);
    const id = p.get("client");
    return id ? { id, name: p.get("name") || "" } : null;
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<LetterDetail | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [edited, setEdited] = useState<string | null>(null); // edited body_text
  const [editing, setEditing] = useState(false);
  const [editedAddress, setEditedAddress] = useState<string | null>(null);
  const [editingAddress, setEditingAddress] = useState(false);
  const [copyOk, setCopyOk] = useState(false);
  const [mailChoice, setMailChoice] = useState<
    { letter: Letter; provider: string; mailType: "first_class" | "certified" } | null
  >(null);
  const [providers, setProviders] = useState<
    { id: string; name: string; configured: boolean; supported_mail_types?: string[] }[]
  >([]);

  async function load() {
    setLoading(true);
    try {
      setRows(await lettersApi.list({
        status: status === "all" || status === "needs_action" ? undefined : status,
        view: status === "needs_action" ? "needs_action" : undefined,
        client_id: clientFilter?.id,
      }));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, clientFilter?.id]);
  useEffect(() => {
    // Providers come back with a `configured` flag so we can show which
    // carriers actually have credentials on this Worker.
    mailApi.list({ limit: 1 })
      .then((r) => setProviders((r.providers as any[]) || []))
      .catch(() => setProviders([]));
  }, []);

  function clearClientFilter() {
    setClientFilter(null);
    // Drop the query string so a refresh doesn't re-apply the filter.
    if (typeof window !== "undefined") {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }

  // Client-side search across the fields an operator actually looks for:
  // client name, letter type, recipient bureau, status, and the reference id.
  const q = search.trim().toLowerCase();
  const visible = !q ? rows : rows.filter((l) => {
    const hay = [
      l.client_name,
      l.client_id,
      l.kind,
      l.kind?.replace(/_/g, " "),
      l.recipient_bureau,
      l.status,
      l.status?.replace(/_/g, " "),
      l.id,
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });

  // PATCH responses omit signature metadata; always re-read the detail route
  // before showing a letter after a mutation (judge finding, 2026-09-10).
  // Generation counter: bumped whenever the preview modal opens/closes, so a
  // refresh that finishes after the operator moved on is dropped.
  const previewGen = useRef(0);
  async function refreshPreview(fallback: LetterDetail, gen: number = previewGen.current): Promise<boolean> {
    if (gen !== previewGen.current) return false;
    const before = preview && preview.id === fallback.id ? preview : null;
    try {
      const detail = await request<{ ok: true; letter: LetterDetail }>(`/api/letters/${fallback.id}`);
      if (gen !== previewGen.current) return false;
      setPreview((detail as any).letter);
      return true;
    } catch {
      if (gen !== previewGen.current) return false;
      // Keep the signature metadata we already had rather than showing the letter as unsigned.
      setPreview({ ...fallback, ...(before ? {
        signature_applied: (before as any).signature_applied, signature_id: (before as any).signature_id, client_id: (before as any).client_id ?? (fallback as any).client_id,
      } : {}) } as LetterDetail);
      setPreviewErr("Saved, but the preview could not refresh — close and reopen the letter to confirm the signature.");
      return true;
    }
  }

  async function openPreview(l: Letter) {
    const gen = ++previewGen.current;
    setPreview(l as LetterDetail);
    setPreviewErr(null);
    setPreviewLoading(true);
    setEdited(null);
    setEditing(false);
    setEditedAddress(null);
    setEditingAddress(false);
    setCopyOk(false);
    setMarkDate(null);
    setMarkNote("");
    try {
      const detail = await request<{ ok: true; letter: LetterDetail }>(`/api/letters/${l.id}`);
      if (gen !== previewGen.current) return;
      setPreview((detail as any).letter);
    } catch (e: any) {
      if (gen !== previewGen.current) return;
      setPreviewErr(e?.message || "Could not load letter");
    } finally { if (gen === previewGen.current) setPreviewLoading(false); }
  }

  async function copyBody() {
    if (!preview?.body_text) return;
    try {
      await navigator.clipboard.writeText(preview.body_text);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 1500);
    } catch (e: any) {
      setPreviewErr(e?.message || "Could not copy to clipboard");
    }
  }

  async function saveAddress() {
    if (!preview || editedAddress === null) return;
    const gen = previewGen.current; const pid = preview.id;
    setBusy(pid);
    try {
      const updated = await request<{ ok: true; letter: LetterDetail }>(`/api/letters/${pid}`, {
        method: "PATCH",
        body: { recipient_address: editedAddress, revision: (preview as any).revision },
      });
      if (!(await refreshPreview((updated as any).letter, gen))) return;
      setEditedAddress(null);
      setEditingAddress(false);
    } catch (e: any) {
      if (gen === previewGen.current) setPreviewErr(e?.message || "Could not save address");
    } finally { setBusy((b) => (b === pid ? null : b)); }
  }

  async function saveBody() {
    if (!preview || edited === null) return;
    const gen = previewGen.current; const pid = preview.id;
    setBusy(pid);
    try {
      const updated = await request<{ ok: true; letter: LetterDetail }>(`/api/letters/${pid}`, {
        method: "PATCH",
        body: { body_text: edited, revision: (preview as any).revision },
      });
      if (!(await refreshPreview((updated as any).letter, gen))) return;
      setEdited(null);
      setEditing(false);
    } catch (e: any) {
      if (gen === previewGen.current) setPreviewErr(e?.message || "Could not save");
    } finally { setBusy((b) => (b === pid ? null : b)); }
  }

  async function deleteLetter(l: Letter) {

    const label = `${l.kind.replace(/_/g, ' ')} · ${l.bureau || (l as any).recipient_bureau || l.recipient || ''}`;

    if (!window.confirm(`Delete this letter?\n\n${label}\n\nA letter that never went out is removed for good. A letter that was already queued or mailed is archived (hidden) instead, because the record of what was sent must be kept.`)) return;

    setBusy(l.id);

    try {

      const r = await lettersApi.remove(l.id);

      if (r.message) window.alert(r.message);

      if (preview?.id === l.id) { previewGen.current++; setPreview(null); }

      await load();

    } catch (e: any) {

      window.alert(e?.message || "Could not delete the letter");

    } finally {

      setBusy(null);

    }

  }


  async function approveLetter(l: LetterDetail) {
    const gen = previewGen.current;
    setBusy(l.id);
    setPreviewErr(null);
    try {
      // Walk the forward ladder: drafting -> quality_gate_pending -> approved.
      // Same-status PATCHes are no-ops server-side, so this is safe to retry.
      let cur = l.status as string;
      // The revision the approver is looking at; each step returns the next one.
      let rev = (l as any).revision as string | undefined;
      if (cur === "drafting") {
        const r1 = await request<{ ok: true; letter: LetterDetail }>(`/api/letters/${l.id}`, {
          method: "PATCH",
          body: { status: "quality_gate_pending", revision: rev },
        });
        cur = (r1 as any).letter?.status || "quality_gate_pending";
        rev = (r1 as any).letter?.revision;
      }
      if (cur === "quality_gate_pending") {
        const r2 = await request<{ ok: true; letter: LetterDetail }>(`/api/letters/${l.id}`, {
          method: "PATCH",
          body: { status: "approved", revision: rev },
        });
        await refreshPreview((r2 as any).letter, gen);
      }
      await load();
    } catch (e: any) {
      if (gen === previewGen.current) setPreviewErr(e?.message || "Could not approve");
    } finally { setBusy((b) => (b === l.id ? null : b)); }
  }

  async function saveMarkMailed() {
    if (!preview || !markDate) return;
    const gen = previewGen.current; const pid = preview.id;
    setBusy(pid); setPreviewErr(null);
    try {
      await lettersApi.markMailed(pid, markDate, markNote.trim(), (preview as any).revision);
      if (!(await refreshPreview(preview, gen))) return;
      setMarkDate(null); setMarkNote("");
      await load();
    } catch (e: any) {
      if (gen === previewGen.current) setPreviewErr(e?.message || "Could not mark the letter mailed");
    } finally { setBusy((b) => (b === pid ? null : b)); }
  }

  async function undoMarkMailed() {
    if (!preview) return;
    if (!window.confirm("Undo \"mailed\" on this letter? It goes back to the needs-action list.")) return;
    const gen = previewGen.current; const pid = preview.id;
    setBusy(pid); setPreviewErr(null);
    try {
      await lettersApi.unmarkMailed(pid);
      await refreshPreview(preview, gen);
      await load();
    } catch (e: any) {
      if (gen === previewGen.current) setPreviewErr(e?.message || "Could not undo");
    } finally { setBusy((b) => (b === pid ? null : b)); }
  }

  // Mailing is a paid, irreversible action, so the operator picks the carrier
  // and service every time — there is deliberately no default provider.
  async function confirmQueueForMail() {
    if (!mailChoice) return;
    const { letter, provider, mailType } = mailChoice;
    setBusy(letter.id);
    setErr(null);
    try {
      await mailApi.queue(letter.id, mailType, provider as any, (letter as any).revision);
      setMailChoice(null);
      await load();
    } catch (e: any) {
      setErr(e?.message || "Could not queue for mail");
    } finally { setBusy(null); }
  }

  return (
    <Shell title="Letters" subtitle="Letter library" actions={<span />}>
      <div className="panel">
        <div className="panel-header">
          <div>
            <h2><FilePenLine size={18} aria-hidden="true" /> Letters</h2>
            {clientFilter && (
              <span className="muted small">
                Showing letters for <strong>{clientFilter.name || clientFilter.id.slice(0, 8)}</strong>
                {" · "}
                <button type="button" className="linklike" onClick={clearClientFilter}>
                  show all clients
                </button>
              </span>
            )}
          </div>
          <label className="search" style={{ marginLeft: "auto" }}>
            <Search size={16} aria-hidden="true" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search client, type, bureau, status…"
              aria-label="Search letters"
            />
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Filter letters by status"
          >
            <option value="needs_action">Needs action</option>
            <option value="all">All letters</option>
            <option value="drafting">Drafting</option>
            <option value="quality_gate_pending">Quality gate pending</option>
            <option value="approved">Approved</option>
            <option value="queued_for_mail">Queued for mail</option>
            <option value="mailed">Mailed</option>
            <option value="responded">Responded</option>
          </select>
        </div>
        {err && <p className="error-text">{err}</p>}
        {loading ? <p className="muted">Loading…</p> : visible.length === 0 ? (
          <EmptyState
            icon={<FileText size={26} />}
            title={search || clientFilter ? "No letters match" : "No letters yet"}
            hint={
              search
                ? `Nothing matches “${search}”. Try a client name, letter type, or bureau.`
                : clientFilter
                  ? "This client has no letters yet. Run their analysis and create a dispute round to draft letters."
                  : "Letters are generated from a client's analysis. Open a client, run the analyzer, then create a dispute round to draft letters."
            }
          />
        ) : (
          <div className="table-list">
            {visible.map((l) => {
              const mailable = MAILABLE.has(l.status);
              const clientLabel = l.client_name || l.client_id.slice(0, 8);
              return (
                <div
                  role="button"
                  tabIndex={0}
                  className="table-row"
                  key={l.id}
                  onClick={() => openPreview(l)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openPreview(l); } }}
                  title="Open letter preview"
                  aria-label={`Open letter ${l.id.slice(0, 8)} for ${clientLabel}`}
                  style={{ cursor: "pointer" }}
                >
                  <span>{l.id.slice(0, 8)}</span>
                  <strong>{clientLabel}</strong>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    {letterKindIcon(l.kind)}{l.kind.replace(/_/g, " ")}
                  </span>
                  <span>{l.bureau || (l as any).recipient_bureau || l.recipient || "—"}</span>
                  <StatusPill status={l.delivered_at && l.status === "mailed" ? "delivered" : l.status} />
                  <span title={l.mailed_at ? `Created ${new Date(l.created_at).toLocaleDateString()}` : undefined}>
                    {l.mailed_at ? `Mailed ${mailDay(l.mailed_at, l.mailed_source === "manual")}` : new Date(l.created_at).toLocaleDateString()}
                  </span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      type="button"
                      className="ghost-button"
                      onClick={(e) => { e.stopPropagation(); openPreview(l); }}
                      title="Preview letter body"
                    >
                      <Eye size={14} aria-hidden="true" /> Open
                    </button>
                    <button
                      type="button"
                      className="ghost-button"
                      disabled={busy === l.id || !mailable}
                      onClick={(e) => { e.stopPropagation(); setMailChoice({ letter: l, provider: "", mailType: "certified" }); }}
                      title={mailable ? "Queue for mail" : "Letter must be approved before it can be queued"}
                    >
                      <Send size={14} aria-hidden="true" /> Mail
                    </button>
                    <button
                      type="button"
                      className="ghost-button"
                      disabled={busy === l.id}
                      onClick={(e) => { e.stopPropagation(); deleteLetter(l); }}
                      title="Delete this letter (a letter that was already mailed is archived instead)"
                      aria-label={`Delete letter ${l.id.slice(0, 8)}`}
                    >
                      <Trash2 size={14} aria-hidden="true" /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Modal
        open={!!preview}
        title={preview ? `${preview.kind} · ${preview.bureau || (preview as any).recipient_bureau || preview.recipient || ""}` : "Letter"}
        onClose={() => { previewGen.current++; setPreview(null); setEdited(null); setEditing(false); setEditedAddress(null); setEditingAddress(false); setPreviewErr(null); setCopyOk(false); setMarkDate(null); setMarkNote(""); }}
        footer={preview ? (
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
            {previewErr && <span className="error-text small" style={{ marginRight: "auto" }}>{previewErr}</span>}
            {editing && edited !== null && (
              <>
                <button className="ghost-button" onClick={() => { setEdited(null); setEditing(false); }}>Cancel</button>
                <button
                  className="primary-button"
                  onClick={saveBody}
                  disabled={busy === preview.id}
                >
                  Save body
                </button>
              </>
            )}
            {!editing && preview.body_text && APPROVABLE_FROM.has(preview.status) && (
              <button
                className="ghost-button"
                onClick={() => { setEdited(preview.body_text || ""); setEditing(true); }}
                title="Edit the letter body before approving"
              >
                Edit
              </button>
            )}
            {!editing && APPROVABLE_FROM.has(preview.status) && (
              <button
                className="primary-button"
                onClick={() => approveLetter(preview)}
                disabled={busy === preview.id || !preview.body_text}
                title={preview.body_text ? "Approve letter for mailing" : "Letter has no body — cannot approve"}
              >
                <CheckCheck size={14} aria-hidden="true" /> Approve for mail
              </button>
            )}
            {!editing && markDate === null && MARKABLE.has(preview.status) && (
              <button
                className="ghost-button"
                onClick={() => setMarkDate(todayLocal())}
                disabled={busy === preview.id || !preview.body_text}
                title="Record that this letter was mailed outside the Hub (LetterStream, post office…)"
              >
                <MailCheck size={14} aria-hidden="true" /> Mark as mailed
              </button>
            )}
            {!editing && markDate === null && preview.status === "mailed" && preview.mailed_source === "manual" && (
              <>
                <button className="ghost-button" onClick={() => { setMarkDate(preview.mailed_at ? mailDayIso(preview.mailed_at) : todayLocal()); setMarkNote(preview.mailed_note || ""); }} disabled={busy === preview.id}>
                  Change mail date
                </button>
                <button className="ghost-button" onClick={undoMarkMailed} disabled={busy === preview.id} title="Marked by mistake? Put it back on the needs-action list">
                  <Undo2 size={14} aria-hidden="true" /> Undo mailed
                </button>
              </>
            )}
            {!editing && markDate === null && MAILABLE.has(preview.status) && (
              <button
                className="primary-button"
                onClick={() => setMailChoice({ letter: preview, provider: "", mailType: "certified" })}
                disabled={busy === preview.id}
              >
                <Send size={14} aria-hidden="true" /> Queue for mail
              </button>
            )}
          </div>
        ) : undefined}
      >
        {previewLoading && <p className="muted small">Loading…</p>}
        {preview && !previewLoading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="identity-grid">
              <div className="field-pair">
                <span className="field-label">Status</span>
                <StatusPill status={preview.status} />
              </div>
              <div className="field-pair">
                <span className="field-label">Bureau / Recipient</span>
                <span>{preview.bureau || (preview as any).recipient_bureau || preview.recipient || "—"}</span>
              </div>
              <div className="field-pair">
                <span className="field-label">Kind</span>
                <span>{preview.kind}</span>
              </div>
              <div className="field-pair">
                <span className="field-label">Created</span>
                <span>{preview.created_at ? new Date(preview.created_at).toLocaleString() : "—"}</span>
              </div>
              <div className="field-pair">
                <span className="field-label">Approved</span>
                <span>{preview.approved_at ? new Date(preview.approved_at).toLocaleString() : "—"}</span>
              </div>
              <div className="field-pair">
                <span className="field-label">Mailed</span>
                <span>
                  {preview.mailed_at
                    ? `${mailDay(preview.mailed_at, preview.mailed_source === "manual")} · ${preview.mailed_source === "manual" ? "marked by hand" : "sent through the Hub"}`
                    : preview.status === "mailed" || preview.status === "responded" ? "Mailed (date not recorded)" : "Not mailed"}
                  {preview.delivered_at ? ` · delivered ${mailDay(preview.delivered_at)}` : ""}
                  {preview.mailed_note ? ` · ${preview.mailed_note}` : ""}
                </span>
              </div>
            </div>

            {markDate !== null && (
              <div className="panel" style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                <strong>{preview.status === "mailed" ? "Change the mail date" : "Mark this letter as mailed"}</strong>
                <span className="muted small">For letters mailed outside the Hub. It moves off the needs-action list but stays in the client's file.</span>
                <div className="form-row-2">
                  <label className="form-field"><span>Date mailed</span>
                    <input type="date" value={markDate} max={todayLocal()} onChange={(e) => setMarkDate(e.target.value)} /></label>
                  <label className="form-field"><span>How it was sent <em className="muted small">(optional)</em></span>
                    <input value={markNote} maxLength={200} onChange={(e) => setMarkNote(e.target.value)} placeholder="e.g. LetterStream certified, tracking #" /></label>
                </div>
                <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                  <button className="ghost-button" onClick={() => { setMarkDate(null); setMarkNote(""); }}>Cancel</button>
                  <button className="primary-button" onClick={saveMarkMailed} disabled={!markDate || busy === preview.id}>
                    <MailCheck size={14} aria-hidden="true" /> Save
                  </button>
                </div>
              </div>
            )}

            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span className="field-label">Recipient address</span>
                {!editingAddress && (
                  <button
                    className="ghost-button"
                    style={{ padding: "2px 8px", fontSize: 12 }}
                    onClick={() => { setEditedAddress(preview.recipient_address || ""); setEditingAddress(true); }}
                    title="Edit the recipient mailing address"
                  >
                    Edit address
                  </button>
                )}
              </div>
              {editingAddress && editedAddress !== null ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                  <textarea
                    value={editedAddress}
                    onChange={(e) => setEditedAddress(e.target.value)}
                    rows={5}
                    className="letter-textarea letter-textarea--address"
                    aria-label="Recipient address"
                    placeholder={"Furnisher Name\nAttn: Consumer Affairs / Dispute Resolution\n123 Main St\nCity, ST 12345"}
                  />
                  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    <button className="ghost-button" onClick={() => { setEditedAddress(null); setEditingAddress(false); }}>
                      Cancel
                    </button>
                    <button
                      className="primary-button"
                      onClick={saveAddress}
                      disabled={busy === preview.id}
                    >
                      Save address
                    </button>
                  </div>
                </div>
              ) : preview.recipient_address ? (
                <pre className="letter-preview-block letter-preview-block--address">
                  {preview.recipient_address}
                </pre>
              ) : (
                <p className="muted small" style={{ marginTop: 4 }}>No recipient address set.</p>
              )}
            </div>

            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span className="field-label">Letter body</span>
                {preview.body_text && !editing && (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      className="ghost-button"
                      style={{ padding: "2px 8px", fontSize: 12 }}
                      onClick={copyBody}
                      title="Copy body text to clipboard"
                    >
                      <ClipboardCopy size={12} aria-hidden="true" /> {copyOk ? "Copied" : "Copy"}
                    </button>
                    <button
                      className="ghost-button"
                      style={{ padding: "2px 8px", fontSize: 12 }}
                      onClick={async () => {
                        if (!preview) return;
                        try {
                          await downloadPdf(
                            `/api/letters/${preview.id}/download`,
                            `letter-${preview.id.slice(0, 8)}.pdf`,
                          );
                        } catch (err: any) {
                          setPreviewErr(err?.message || "Download failed");
                        }
                      }}
                      title="Download this letter as a clean black-and-white PDF"
                    >
                      <Download size={12} aria-hidden="true" /> Download PDF
                    </button>
                    {(() => {
                      const isBundleKind = BUREAU_BUNDLE_KINDS.has(preview.kind);
                      const bureau = (preview.bureau || (preview as any).recipient_bureau || "").toLowerCase();
                      const roundId = preview.round_id || preview.dispute_round_id;
                      if (!isBundleKind || !bureau || !roundId) return null;
                      const label =
                        bureau === "experian" ? "Experian" : bureau === "transunion" ? "TransUnion" : "Equifax";
                      return (
                        <button
                          className="ghost-button"
                          style={{ padding: "2px 8px", fontSize: 12 }}
                          onClick={async () => {
                            try {
                              await downloadPdf(
                                `/api/dispute-rounds/${roundId}/bundle?bureau=${bureau}`,
                                `${bureau}-bundle.pdf`,
                              );
                            } catch (err: any) {
                              setPreviewErr(err?.message || `Could not download ${label} bundle`);
                            }
                          }}
                          title={`Download the full ${label} bundle for this round (all bureau letters + intake docs)`}
                        >
                          <FileDown size={12} aria-hidden="true" /> {label} bundle
                        </button>
                      );
                    })()}
                  </div>
                )}
              </div>
              {editing && edited !== null ? (
                <textarea
                  value={edited}
                  onChange={(e) => setEdited(e.target.value)}
                  rows={20}
                  className="letter-textarea"
                  aria-label="Letter body"
                  style={{ marginTop: 4 }}
                />
              ) : preview.body_text ? (
                (() => {
                  // Mirror the PDF renderer: split at the LAST "\nSincerely,\n" and place the
                  // ink signature between the close and the typed name. body_text itself is
                  // untouched (Copy / Edit still use the original).
                  const body = preview.body_text;
                  const signed = !!(preview as any).signature_applied && !!(preview as any).client_id;
                  const closeAt = signed ? body.lastIndexOf("\nSincerely,\n") : -1;
                  if (!signed || closeAt < 0) {
                    return <pre className="letter-preview-block" style={{ marginTop: 4 }}>{body}</pre>;
                  }
                  const before = body.slice(0, closeAt);
                  const after = body.slice(closeAt + "\nSincerely,\n".length).replace(/^(?:[ \t]*\r?\n)+/, "");
                  return (
                    <pre className="letter-preview-block" style={{ marginTop: 4 }}>
                      {before}{"\nSincerely,\n"}
                      <ClientSignatureImg clientId={(preview as any).client_id} version={(preview as any).signature_id} className="letter-signature-preview" alt="Client signature that prints on this letter" />
                      {after}
                    </pre>
                  );
                })()
              ) : (
                <p className="muted small" style={{ marginTop: 4 }}>
                  No body yet. The letter builder hasn't produced content for this draft.
                </p>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Carrier + service picker. No provider is preselected — the operator
          chooses Lob or LetterStream deliberately for every mailing. */}
      <Modal
        open={!!mailChoice}
        title="Send this letter"
        onClose={() => setMailChoice(null)}
        footer={
          <>
            <button className="btn ghost" onClick={() => setMailChoice(null)}>Cancel</button>
            <button
              className="btn primary"
              disabled={!mailChoice?.provider || busy === mailChoice?.letter.id}
              onClick={confirmQueueForMail}
            >
              {busy === mailChoice?.letter.id ? "Queueing…" : "Queue for mail"}
            </button>
          </>
        }
      >
        {mailChoice && (
          <div className="stack">
            <p className="muted small">
              {mailChoice.letter.kind?.replace(/_/g, " ")} ·{" "}
              {mailChoice.letter.client_name || mailChoice.letter.client_id?.slice(0, 8)}
              {mailChoice.letter.recipient_bureau ? ` · ${mailChoice.letter.recipient_bureau}` : ""}
            </p>

            <div>
              <label className="label">Mail carrier</label>
              <div className="stack" style={{ gap: 8 }}>
                {providers.length === 0 && (
                  <p className="muted small">Loading carriers…</p>
                )}
                {providers.map((p) => (
                  <label
                    key={p.id}
                    className="choice-row"
                    style={{
                      display: "flex", alignItems: "flex-start", gap: 10,
                      padding: 10, border: "1px solid var(--line, #2a2a3a)", borderRadius: 8,
                      opacity: p.configured ? 1 : 0.55,
                      cursor: p.configured ? "pointer" : "not-allowed",
                    }}
                  >
                    <input
                      type="radio"
                      name="mail-provider"
                      value={p.id}
                      disabled={!p.configured}
                      checked={mailChoice.provider === p.id}
                      onChange={() =>
                        setMailChoice((m) => (m ? { ...m, provider: p.id } : m))
                      }
                    />
                    <span>
                      <strong>{p.name}</strong>
                      <span className="muted small" style={{ display: "block" }}>
                        {p.configured
                          ? (p.supported_mail_types || []).map((t) => t.replace(/_/g, " ")).join(" · ")
                          : "Not configured — add this provider's API credentials first"}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="label">Service</label>
              <select
                className="input"
                value={mailChoice.mailType}
                onChange={(e) =>
                  setMailChoice((m) =>
                    m ? { ...m, mailType: e.target.value as "first_class" | "certified" } : m,
                  )
                }
              >
                <option value="certified">Certified mail (tracked — recommended for disputes)</option>
                <option value="first_class">First class</option>
              </select>
            </div>

            {err && <p className="error">{err}</p>}
          </div>
        )}
      </Modal>
    </Shell>
  );
}
